import { useState } from "react";

const CATEGORIES = ["Technical", "Operational", "Financial", "Regulatory", "Strategic"];

const EMPTY = {
  name: "", cat: "Technical", owner: "",
  prob: "", imp: "", sev: "", occ: "", det: "", mit: "",
};

export default function RiskForm({ addRisk }) {
  const [form, setForm]     = useState(EMPTY);
  const [errors, setErrors] = useState({});

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: "" }));
  };

  const liveScore = form.prob && form.imp ? Number(form.prob) * Number(form.imp) : null;
  const liveRPN   = form.sev && form.occ && form.det
    ? Number(form.sev) * Number(form.occ) * Number(form.det) : null;

  const scoreClass = (v, hi, mid) =>
    v == null ? "" : v > hi ? "lvl-danger" : v > mid ? "lvl-warn" : "lvl-ok";

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    const p = Number(form.prob), i = Number(form.imp);
    if (!form.prob || p < 1 || p > 5) e.prob = "1–5";
    if (!form.imp  || i < 1 || i > 5) e.imp  = "1–5";
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleAdd = () => {
    if (!validate()) return;
    addRisk({
      name:  form.name.trim(),
      cat:   form.cat,
      owner: form.owner.trim(),
      prob:  Number(form.prob),
      imp:   Number(form.imp),
      sev:   Number(form.sev) || 5,
      occ:   Number(form.occ) || 5,
      det:   Number(form.det) || 5,
      mit:   form.mit.trim(),
    });
    setForm(EMPTY);
    setErrors({});
  };

  return (
    <div className="form-wrap">
      {/* Live preview */}
      <div className="form-section-label" style={{ marginTop: 0 }}>Live Preview</div>
      <div className="live-preview">
        <div className="lp-card">
          <div className="lp-label">Score</div>
          <div className={`lp-val ${scoreClass(liveScore, 15, 8)}`}>{liveScore ?? "—"}</div>
        </div>
        <div className="lp-card">
          <div className="lp-label">RPN</div>
          <div className={`lp-val ${scoreClass(liveRPN, 500, 200)}`}>{liveRPN ?? "—"}</div>
        </div>
      </div>

      {/* Core fields */}
      <div className="form-section-label">Add Risk</div>

      <div className="field">
        <label>Risk Name *</label>
        <input
          placeholder="e.g. Server Outage"
          value={form.name}
          onChange={(e) => set("name", e.target.value)}
          className={errors.name ? "err-border" : ""}
        />
        {errors.name && <div className="field-err">{errors.name}</div>}
      </div>

      <div className="field-row2">
        <div className="field">
          <label>Category</label>
          <select value={form.cat} onChange={(e) => set("cat", e.target.value)}>
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Owner</label>
          <input placeholder="Name / team" value={form.owner} onChange={(e) => set("owner", e.target.value)} />
        </div>
      </div>

      <div className="field-row2">
        <div className="field">
          <label>Probability (1–5) *</label>
          <input
            type="number" min={1} max={5} placeholder="1–5"
            value={form.prob}
            onChange={(e) => set("prob", e.target.value)}
            className={errors.prob ? "err-border" : ""}
          />
          {errors.prob && <div className="field-err">{errors.prob}</div>}
        </div>
        <div className="field">
          <label>Impact (1–5) *</label>
          <input
            type="number" min={1} max={5} placeholder="1–5"
            value={form.imp}
            onChange={(e) => set("imp", e.target.value)}
            className={errors.imp ? "err-border" : ""}
          />
          {errors.imp && <div className="field-err">{errors.imp}</div>}
        </div>
      </div>

      {/* FMEA */}
      <div className="form-section-label">FMEA Scores (1–10)</div>
      <div className="field-row3">
        {[["sev","Severity"],["occ","Occurrence"],["det","Detection"]].map(([k, label]) => (
          <div className="field" key={k}>
            <label>{label}</label>
            <input
              type="number" min={1} max={10} placeholder="1–10"
              value={form[k]}
              onChange={(e) => set(k, e.target.value)}
            />
          </div>
        ))}
      </div>

      {/* Mitigation */}
      <div className="field">
        <label>Mitigation Notes</label>
        <textarea
          rows={2}
          placeholder="Optional — or generate via AI after adding"
          value={form.mit}
          onChange={(e) => set("mit", e.target.value)}
        />
      </div>

      <div className="sidebar-footer">
        <button className="btn btn-primary btn-full" onClick={handleAdd}>
          + Add Risk
        </button>
      </div>
    </div>
  );
}